import React from 'react';
import { FiClock, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { HighlightStatCard } from '@components/HighlightStatCard';
import lateEarlyAlertsService from '@/services/lateEarlyAlerts';

import styles from './StatsRow.module.scss';

const pctDelta = (today, yesterday) => {
  if (!yesterday) return null;
  return Math.round(((today - yesterday) / yesterday) * 100);
};

const avg = (total, count) => (count > 0 ? Math.round(total / count) : 0);

/**
 * Six stat cards, all sourced from the same handful of already-fetched
 * rollup docs (today + last 7 days) — no per-employee reads anywhere here.
 * Uses HighlightStatCard (@components/HighlightStatCard) — the recreated
 * DashboardOverviewNew "Key Highlights" card recipe — not TrendStatCard,
 * which is a different card family used by App Usage / URL Tracking. Plain
 * white cards, no tinted backgrounds, matching User Dashboard / Department
 * Dashboard exactly.
 */
export const StatsRow = ({ rollups, isLoading }) => {
  const today = rollups[rollups.length - 1] || {};
  const yesterday = rollups[rollups.length - 2] || {};

  const lateCount = Number(today.lateCount) || 0;
  const earlyCount = Number(today.earlyCount) || 0;
  const criticalCount = Number(today.criticalCount) || 0;
  // presentCount comes from the older, separate productivity-tracking job
  // and exists for every day regardless of whether login alerts were ever
  // configured — it must never be used to back into an "everyone was on
  // time" count for a day the login-alert trigger never actually ran on.
  // hasLoginAlertData gates that: no real late/early data for the day means
  // no on-time rate either, not a false 100%.
  const presentCount = Number(today.presentCount) || 0;
  const onTimeCount = lateEarlyAlertsService.hasLoginAlertData(today)
    ? Math.max(0, presentCount - lateCount - earlyCount)
    : 0;
  const onTimeRate =
    lateEarlyAlertsService.hasLoginAlertData(today) && presentCount > 0
      ? Math.round((onTimeCount / presentCount) * 100)
      : 0;
  const yesterdayPresentCount = Number(yesterday.presentCount) || 0;
  const yesterdayOnTimeCount = lateEarlyAlertsService.hasLoginAlertData(
    yesterday
  )
    ? Math.max(
        0,
        yesterdayPresentCount -
          (Number(yesterday.lateCount) || 0) -
          (Number(yesterday.earlyCount) || 0)
      )
    : 0;
  const yesterdayOnTimeRate =
    lateEarlyAlertsService.hasLoginAlertData(yesterday) &&
    yesterdayPresentCount > 0
      ? Math.round((yesterdayOnTimeCount / yesterdayPresentCount) * 100)
      : 0;

  const avgLate = avg(Number(today.totalLateMinutes) || 0, lateCount);
  const avgEarly = avg(Number(today.totalEarlyMinutes) || 0, earlyCount);
  const yesterdayAvgLate = avg(
    Number(yesterday.totalLateMinutes) || 0,
    Number(yesterday.lateCount) || 0
  );
  const yesterdayAvgEarly = avg(
    Number(yesterday.totalEarlyMinutes) || 0,
    Number(yesterday.earlyCount) || 0
  );

  // For every card except On-Time Rate, an increase is the bad outcome
  // (more lateness/earliness/critical delays), so "up" reads as a warning
  // (red) rather than a win (green) — trendSentiment carries that meaning
  // independent of which way the arrow literally points.
  const cards = [
    {
      title: 'Late Today',
      value: lateCount,
      icon: <FiClock size={16} color="var(--error-600)" />,
      iconBg: 'var(--error-100)',
      rawDelta: pctDelta(lateCount, Number(yesterday.lateCount) || 0),
      trendSentiment: 'negative',
    },
    {
      title: 'Early Today',
      value: earlyCount,
      icon: <FiClock size={16} color="var(--success-600)" />,
      iconBg: 'var(--success-100)',
      rawDelta: pctDelta(earlyCount, Number(yesterday.earlyCount) || 0),
      trendSentiment: 'negative',
    },
    {
      title: 'Avg. Late Arrival',
      value: `${avgLate} min`,
      icon: <FiClock size={16} color="var(--blue-600)" />,
      iconBg: 'var(--blue-100)',
      rawDelta: pctDelta(avgLate, yesterdayAvgLate),
      trendSentiment: 'negative',
    },
    {
      title: 'Avg. Early Arrival',
      value: `${avgEarly} min`,
      icon: <FiClock size={16} color="var(--purple-600)" />,
      iconBg: 'var(--purple-100)',
      rawDelta: pctDelta(avgEarly, yesterdayAvgEarly),
      trendSentiment: 'negative',
    },
    {
      title: 'Critical Delays',
      value: criticalCount,
      tooltip: 'Check-ins more than the critical threshold late.',
      icon: <FiAlertTriangle size={16} color="var(--error-600)" />,
      iconBg: 'var(--error-100)',
      rawDelta: criticalCount - (Number(yesterday.criticalCount) || 0),
      trendSentiment: 'negative',
      trendSuffix: '',
    },
    {
      title: 'On-Time Rate',
      value: `${onTimeRate}%`,
      icon: <FiCheckCircle size={16} color="var(--success-600)" />,
      iconBg: 'var(--success-100)',
      rawDelta: onTimeRate - yesterdayOnTimeRate,
      trendSentiment: 'positive',
    },
  ];

  return (
    <div className={styles.statsRow}>
      {cards.map((card) => (
        <HighlightStatCard
          key={card.title}
          label={card.title}
          tooltip={card.tooltip}
          value={card.value}
          icon={card.icon}
          iconBg={card.iconBg}
          trendPct={card.rawDelta != null ? card.rawDelta : null}
          trendSuffix={card.trendSuffix}
          trendSentiment={card.trendSentiment}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
};

StatsRow.displayName = 'StatsRow';
